import { Injectable } from '@angular/core';
import { Userpilot } from 'userpilot';

import { BackupPaymentStatus } from '../../interfaces/payment-method';

/**
 * Event the in-app backup-payment nudge is built on (OPS-2216).
 *
 * The name is the contract with the UserPilot console: the flow triggers on it, so it cannot
 * be renamed here alone. It still reads `missing` although OPS-2288 widened it to carry a
 * declined card too — the string is what the console matches on, and renaming it would silently
 * detach the live flow.
 *
 * Metadata rides along for reporting: `leaseKey` answers "which leases did this fire for", and
 * `backupPaymentStatus` answers "and what was wrong with them". Neither drives targeting —
 * UserPilot segments and frequency-caps on the event name, not on its payload — so a flow that
 * needs to treat a missing card differently from a declined one cannot do it from here.
 */
export const LEASE_BACKUP_EVENT = 'lease_backup_missing';

/**
 * Everything the portal says to UserPilot that is not a user property.
 *
 * Owns two things a component cannot: which leases have already been nudged, and the point at
 * which the whole UserPilot session ends.
 */
@Injectable({ providedIn: 'root' })
export class UserpilotService {

    readonly #nudgedLeases = new Set<string>();

    /**
     * Tells UserPilot she is looking at a pilot lease whose backup payment method needs her —
     * either because there is none on file, or because the last charge against it was declined
     * (OPS-2288). The status says which.
     *
     * Fires once per lease per session, whichever of the two it was. The contract sheet is
     * destroyed and rebuilt on every lease navigation, so a latch held there would reset each
     * time she moved between leases and fire again on the way back. Deduplication has to live on
     * this side regardless: UserPilot's own frequency caps are per flow per user, not per event
     * payload, so it cannot express "once for each of her leases".
     *
     * Latched per lease rather than per lease-and-status, because within one session a lease
     * only ever improves: acting on either message leaves her `active`, which is not a state
     * this fires on at all.
     *
     * Returns whether it actually fired, which is what the tests assert on — the alternative is
     * reaching into UserPilot to find out.
     */
    public nudgeBackupNeedsAttention(leaseKey: string, backupPaymentStatus: BackupPaymentStatus): boolean {
        if (!leaseKey || this.#nudgedLeases.has(leaseKey)) {
            return false;
        }

        this.#nudgedLeases.add(leaseKey);
        Userpilot.track(LEASE_BACKUP_EVENT, { leaseKey, backupPaymentStatus });
        return true;
    }

    /**
     * Ends the UserPilot session at logout: forget the ROSE, drop the connection, forget which
     * of her leases were nudged.
     *
     * `identify` is sticky — without `reset` the SDK still believes the previous ROSE is here,
     * so on a shared browser the next person's activity lands on her record and flows aimed at
     * her can fire for them. That includes an agent signing into Admin View, whom we take care
     * never to identify in the first place.
     *
     * `destroy` then closes the socket rather than leaving it to be severed on unload.
     *
     * **This is one-way.** The npm wrapper loads the SDK exactly once — `initialize` is guarded
     * by a module-level flag it never clears — so nothing can bring UserPilot back in this
     * document afterwards, and later calls would queue against a script-load event that has
     * already fired and never be sent. It is therefore only safe to call immediately before the
     * page is replaced, which is exactly what the logout effect does. Both calls are guarded:
     * a missing method on the vendor object must not be what stops a ROSE logging out.
     */
    public endSession(): void {
        this.#nudgedLeases.clear();

        try {
            Userpilot.reset();
        } catch {
            // Nothing to forget is not a failure worth blocking logout for.
        }

        try {
            Userpilot.destroy();
        } catch {
            // Same: the page is about to go anyway, taking the socket with it.
        }
    }
}
